/* eslint-disable func-call-spacing */
/* eslint-disable no-spaced-func */

import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import Divider from '@material-ui/core/Divider';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

// React Swipeable Views
import SwipeableViews from 'react-swipeable-views';

// React CSV
import { CSVLink } from 'react-csv';

// Material Theme
import { useTheme } from '@material-ui/core/styles';

// Actions
import { setOpen, setClose } from 'store/ducks/dialogs/actions';
import * as ReportActions from 'store/ducks/reports/actions';
import * as SellingTerminalsActions from 'store/ducks/sellingTerminals/actions';

// Types
import { ApplicationState } from 'store';
import {
  PaymentInterface,
  TrasactionInterface,
} from 'store/ducks/employeesSales/types';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { formatDateAndTime } from 'helpers/date';
import { Capitalize } from 'helpers/capitalize';
import { maskCPF } from 'helpers/maskers';
import { getDaysLimitToReportDurable } from 'helpers/localConfig';
import { promiseDispatch } from 'store/utils/promiseAction';

// Constant
import { REPORTS_TYPES } from 'constants/report';
import IMAGES from 'constants/images';
import ITEMS from 'constants/itemsByFilter';
import CONST_QRCODE from 'constants/qrcodes';
import CONST_SELLING_POINTS from 'constants/sellingPoints';
import TITLES_OPTIONS from 'constants/infoModulesTitle';
import { REPORT_GROUPINGS } from 'constants/sellingTerminals';
import CONST_TRANSPORT_VOUCHER_COLLECTION from 'constants/transportVoucherCollectionReport';

// Components
import { CustomTabs } from 'components/ReportComponents';
import { TabPanel } from 'components/TabPanel';
import {
  MainContent,
  DialogueExport,
  SearchComponent,
  XlsHeaderInterface,
  XlsRowInterface,
  ReportTitle,
} from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';

// Styles
import useStyle from './CollectionReportDatails.styles';
import { HandleSubmitInterface } from '../../../OnboardSalesReport';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));
const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);
const SellingPaymentNavigation = lazy(() =>
  import('./components/SellingPaymentNavigation').then((mod) => ({
    default: mod.SellingPaymentNavigation,
  })),
);
const SellingTransactionNavigation = lazy(() =>
  import('./components/SellingTransactionNavigation').then((mod) => ({
    default: mod.SellingTransactionNavigation,
  })),
);
const ReportDurableMessage = lazy(() =>
  import('components/ReportDurableMessage/ReportDurableMessage'),
);
const GroupingsModal = lazy(() =>
  import('./components/GroupingsModal').then((mod) => ({
    default: mod.GroupingsModal,
  })),
);
const Submissions = lazy(() =>
  import('../../../../components').then((mod) => ({
    default: mod.Submissions,
  })),
);
const History = lazy(() =>
  import('../../../../components').then((mod) => ({ default: mod.History })),
);
interface CollectionReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

type Props = CollectionReportDatailsProps;

/* eslint-disable no-confusing-arrow */
const CollectionReportDatails: React.FC<Props> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const theme = useTheme();
  const classes = useStyle();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [tableCSV, setTableCSV] = useState('');
  const [currNavGuide, setCurrNavGuide] = useState(0);
  const [tableReference, setTableReference] = useState();
  const [groupingsDialog, setGroupingsDialog] = useState(false);
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const storageDaysLimitToReportDurable = JSON.parse(
    getDaysLimitToReportDurable(),
  );
  const [paymentGroupings, setPaymentGroupings] = useState<string[]>([]);
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);
  const [paymentsList, setPaymentsList] = useState<PaymentInterface[]>();
  const [transactionsList, setTransactionsList] = useState<
    TrasactionInterface[]
  >();

  const [transactionGroupings, setTransactionGroupings] = useState<string[]>(
    [],
  );

  const hasKey = ['sellingPointId', 'sellingTerminalId', 'responsible'];
  const [periodString, setPeriodString] = useState('');
  const [paymentOrder, setPaymentOrder] = useState<Order>('asc');
  const [paymentOrderBy, setPaymentOrderBy] = useState<string>('amount');
  const [transactionOrder, setTransactionOrder] = useState<Order>('asc');
  const [transactionOrderBy, setTransactionOrderBy] = useState<string>(
    'amount',
  );
  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const [controlFilters, setControlFilters] = useState<HandleSubmitInterface>();

  const initialPaymentsList = useSelector(
    (state: ApplicationState) => state.sellingTerminals.paymentsList,
  );

  const initialTransactionsList = useSelector(
    (state: ApplicationState) => state.sellingTerminals.transactionsList,
  );

  const sellingPointList = useSelector(
    (state: ApplicationState) => state.sellingPoints.sellingPointsList,
  );

  const sellingTerminalList = useSelector(
    (state: ApplicationState) => state.sellingTerminals.sellingTerminalsList,
  );

  const startDate = useSelector(
    (state: ApplicationState) => state.reports.selectedStartDate,
  );

  const endDate = useSelector(
    (state: ApplicationState) => state.reports.selectedEndDate,
  );

  const startedAt = useSelector(
    (state: ApplicationState) => state.reports.startedAt,
  );

  const selectedSellingPoint = useSelector(
    (state: ApplicationState) => state.reports.selectedSellingPoint,
  );

  const selectedCreditType = useSelector(
    (state: ApplicationState) => state.reports.selectedCreditType,
  );

  const selectedPaymentType = useSelector(
    (state: ApplicationState) => state.companies.selectedPaymentType,
  );

  const selectedQRCodes = useSelector(
    (state: ApplicationState) => state.reports.selectedQRCodes,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const paymentOptions = useMemo(
    () => [
      {
        key: 'usedDate',
        value: 'Data',
      },
      {
        key: 'amount',
        value: 'Valor',
      },
    ],
    [],
  );

  const transactionOptions = useMemo(
    () => [
      {
        key: 'usedDate',
        value: 'Data',
      },
      {
        key: 'type',
        value: 'Tipo de Crédito',
      },
      {
        key: 'sellingPointId',
        value: 'Ponto de Venda',
      },
      {
        key: 'sellingTerminalId',
        value: 'Rede de Venda',
      },
      {
        key: 'amount',
        value: 'Valor',
      },
      {
        key: 'lineSectionId',
        value: 'Linha',
      },
      {
        key: 'paymentType',
        value: 'Forma de recebimento ',
      },
      {
        key: 'qrcodeType',
        value: 'Tipo do QRCode',
      },
    ],
    [],
  );

  const getSellingName = (transaction: any, key: string): string => {
    const sellingPointName = sellingPointList.find(
      ({ id }) => id === transaction.sellingPointId,
    );

    const sellingTerminalName = sellingTerminalList.find(
      ({ id }) => id === transaction.sellingTerminalId,
    );

    if (key === 'sellingPointId') return sellingPointName?.name || '';
    if (key === 'sellingTerminalId') return sellingTerminalName?.name || '';
    return sellingPointName?.name || sellingTerminalName?.name || '';
  };

  // Formatters
  const formatDateTime = useMemo(
    () => (date: Date) => date && new Date(date).toLocaleString('pt-BR'),
    [],
  );

  const formatAmount = useMemo(
    () => (amount: number | string) => {
      if (typeof amount === 'number') {
        return `R$ ${amount.toFixed(2)}`;
      }
      return `R$ ${!Number.isNaN(Number(amount)) ? Number(amount).toFixed(2) : 0
        }`;
    },
    [],
  );

  const formatQrcodeType = useMemo(
    () => (qrcodeType: number) =>
      CONST_QRCODE.ENTRY.find((e) => e.KEY === qrcodeType)?.VALUE || '--',
    [],
  );

  const formatSellingPointName = useMemo(
    () => (sellingPointId: string) =>
      sellingPointList.find((e) => e.id === sellingPointId)?.name || '--',
    [sellingPointList],
  );

  const formatDescription = useMemo(
    () => (description: string) => description,
    [],
  );

  const formatDeviceModel = useMemo(
    () => (deviceModel: string) => deviceModel,
    [],
  );

  const formatSellingTerminalPlace = useMemo(
    () => (sellingTerminalPlace: string) => sellingTerminalPlace,
    [],
  );

  const formatPaymentType = (paymentType: string) =>
    CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find(
      (e) => e.KEY === paymentType,
    )?.VALUE || '--';

  const formatType = (paymentType: number) =>
    Capitalize(
      CONST_SELLING_POINTS.TRANSACTION_TYPES.find((e) => e.KEY === paymentType)
        ?.VALUE || '--',
    );

  // Field to Formatter Mapping
  const fieldFormatting = new Map<string, (arg: any) => any>([
    ['date', formatDateTime],
    ['amount', formatAmount],
    ['paymentValue', formatAmount],
    ['totalPaymentValue', formatAmount],
    ['qrcodeType', formatQrcodeType],
    ['totalValue', formatAmount],
    ['paymentType', formatPaymentType],
    ['sellingPointId', formatSellingPointName],
    ['sellingTerminalPlace', formatSellingTerminalPlace],
    ['description', formatDescription],
    ['deviceModel', formatDeviceModel],
  ]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(async () => {
    setReportIsVisible(true);

    const eDate = new Date();
    const sDate = new Date(eDate);
    sDate.setDate(sDate.getDate() - 3);

    dispatch(
      ReportActions.setSelectedStartDate({
        selectedStartDate: new Date(),
      }),
    );

    dispatch(
      SellingTerminalsActions.listPaymentsRequest({
        _includes: 'supervisor,terminal',
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch]);

  const getCurrentNavigation = useCallback(
    () => (currNavGuide === 0 ? 'repasses' : 'transacoes'),
    [currNavGuide],
  );

  const updateReport = async (groups: string[]) => {
    const params = {};
    const isPeriod = startDate?.getTime() < endDate?.getTime();

    if (groups && groups.length > 0) {
      Object.assign(params, params, { _group: groups.join() });
    } else {
      Object.assign(params, params, {});
    }

    Object.assign(params, params, {
      startDate:
        !isPeriod && startedAt
          ? new Date(startedAt).setHours(0, 0, 0)
          : new Date(startDate).getTime(),
      endDate:
        !isPeriod && startedAt
          ? new Date(startedAt).setHours(23, 59, 59)
          : new Date(endDate).getTime(),
      sellingPointId:
        selectedSellingPoint?.length > 0
          ? selectedSellingPoint.map((point: any) => point.id).join()
          : undefined,
      transactionType:
        currNavGuide === 1 && selectedCreditType?.length > 0
          ? selectedCreditType.map((e: any) => e.key).join()
          : undefined,
      paymentType:
        currNavGuide === 1 && selectedPaymentType?.length > 0
          ? selectedPaymentType.map((e: any) => e.KEY).join()
          : undefined,
      qrcodeType:
        currNavGuide === 1 && selectedQRCodes?.length > 0
          ? selectedQRCodes.map((e: any) => e.key).join()
          : undefined,
    });

    if (currNavGuide === 0) {
      dispatch(
        SellingTerminalsActions.listPaymentsRequest({
          ...Object.assign(params, { _includes: 'supervisor,terminal' }),
        }),
      );
    }

    if (currNavGuide === 1) {
      dispatch(
        SellingTerminalsActions.listTransactionsRequest({
          ...params,
        }),
      );
    }
  };

  const handleApply = useCallback(
    async (data: any) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const isPeriod = data?.startDate?.getTime() < data?.endDate?.getTime();

      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(data?.startDate).getTime();
        endDateValue = new Date(data?.endDate).getTime();
      } else if (startedAt || data?.startedAt) {
        startDateValue = new Date(data?.startedAt).setHours(0, 0, 0);
        endDateValue = new Date(data?.startedAt).setHours(23, 59, 59);
      }

      dispatch(
        SellingTerminalsActions.listPaymentsRequest({
          _includes: 'supervisor,terminal',
          startDate: startDateValue,
          endDate: endDateValue,
          sellingPointId:
            data.selectedSellingPoint?.length > 0
              ? data.selectedSellingPoint.map((point: any) => point.id).join()
              : undefined,
          sellingTerminalId:
            data.selectedSellingTerminal?.length > 0
              ? data.selectedSellingTerminal
                .map((point: any) => point.id)
                .join()
              : undefined,
          transactionType:
            data.selectedCreditType?.length > 0
              ? data.selectedCreditType.map((e: any) => e.key).join()
              : undefined,
          paymentType:
            data.selectedPaymentType?.length > 0
              ? data.selectedPaymentType.map((e: any) => e.KEY).join()
              : undefined,
          qrcodeType:
            data.selectedQRCodes?.length > 0
              ? data.selectedQRCodes.map((e: any) => e.key).join()
              : undefined,
          _group: paymentGroupings.join() ? paymentGroupings.join() : '',
        }),
      );

      const daysInFilteredPeriod = isPeriod
        ? Math.floor(
          (new Date(data?.endDate).setHours(0, 0, 0, 0) -
            new Date(data?.startDate).setHours(0, 0, 0, 0)) /
          (1000 * 60 * 60 * 24),
        ) + 1
        : 1;

      // Grouped transactions stay synchronous: the asynchronous report is split
      // by period and each part would carry its own partial totals
      const isAsynchronousTransactions =
        !transactionGroupings.length &&
        daysInFilteredPeriod > storageDaysLimitToReportDurable;

      if (isAsynchronousTransactions) {
        try {
          await promiseDispatch(
            ReportActions.reportDurableClientRequest({
              startDate: startDateValue,
              endDate: endDateValue,
              sellingPointId:
                data.selectedSellingPoint?.length > 0
                  ? data.selectedSellingPoint.map((point: any) => point.id).join()
                  : undefined,
              sellingTerminalId:
                data.selectedSellingTerminal?.length > 0
                  ? data.selectedSellingTerminal
                    .map((point: any) => point.id)
                    .join()
                  : undefined,
              transactionType:
                data.selectedCreditType?.length > 0
                  ? data.selectedCreditType.map((e: any) => e.key).join()
                  : undefined,
              paymentType:
                data.selectedPaymentType?.length > 0
                  ? data.selectedPaymentType.map((e: any) => e.KEY).join()
                  : undefined,
              qrcodeType:
                data.selectedQRCodes?.length > 0
                  ? data.selectedQRCodes.map((e: any) => e.key).join()
                  : undefined,
              reportType: REPORTS_TYPES.SELLING_TRANSACTION,
            }),
          );

          dispatch(
            setOpen({
              open: true,
              content: (
                <Suspense fallback={'Carregando...'}>
                  <ReportDurableMessage onClose={() => dispatch(setClose({}))} />
                </Suspense>
              ),
              maxWidth: 'lg',
            }),
          );
        } catch (error) {
          // erro já tratado no saga
        }
      } else {
        dispatch(
          SellingTerminalsActions.listTransactionsRequest({
            startDate: startDateValue,
            endDate: endDateValue,
            sellingPointId:
              data.selectedSellingPoint?.length > 0
                ? data.selectedSellingPoint.map((point: any) => point.id).join()
                : undefined,
            sellingTerminalId:
              data.selectedSellingTerminal?.length > 0
                ? data.selectedSellingTerminal
                  .map((point: any) => point.id)
                  .join()
                : undefined,
            transactionType:
              data.selectedCreditType?.length > 0
                ? data.selectedCreditType.map((e: any) => e.key).join()
                : undefined,
            paymentType:
              data.selectedPaymentType?.length > 0
                ? data.selectedPaymentType.map((e: any) => e.KEY).join()
                : undefined,
            qrcodeType:
              data.selectedQRCodes?.length > 0
                ? data.selectedQRCodes.map((e: any) => e.key).join()
                : undefined,
            _group: transactionGroupings.join()
              ? transactionGroupings.join()
              : '',
          }),
        );
      }

      const tempPeriodString = `De: ${isPeriod
        ? formatDateAndTime(new Date(data?.startDate))
        : formatDateAndTime(new Date(data?.startedAt))
        } até: ${isPeriod
          ? formatDateAndTime(new Date(data?.endDate))
          : formatDateAndTime(new Date(data?.startedAt))
        }`;

      let updatedPeriodString = tempPeriodString;

      if (data.selectedSellingPoint?.length > 0) {
        const tempLinesString = `Ponto de Venda: ${data.selectedSellingPoint
          ?.map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (data.selectedSellingTerminal?.length > 0) {
        const tempLinesString = `Rede de Venda: ${data.selectedSellingTerminal
          ?.map((e: any) => e.name)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (data.selectedCreditType?.length > 0) {
        const tempLinesString = `Forma de Crédito: ${data.selectedCreditType
          ?.map((e: any) => e.value)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (data.selectedPaymentType?.length > 0) {
        const tempLinesString = `Forma de Pagamento: ${data.selectedPaymentType
          ?.map((e: any) => e.VALUE)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      if (data.selectedQRCodes?.length > 0) {
        const tempLinesString = `Tipo de QRCode: ${data.selectedQRCodes
          ?.map((e: any) => e.value)
          .join(', ')}`;
        updatedPeriodString += ', ' + tempLinesString;
      }

      setPeriodString(updatedPeriodString);
      setControlFilters(data);
    },
    [currNavGuide, transactionGroupings, updateReport, storageDaysLimitToReportDurable],
  );

  const handlePaymentGrouping = (groups: Array<string>) => {
    setPaymentGroupings(groups);
    updateReport(groups);
    setGroupingsDialog(false);
  };

  const handleTransactionGrouping = useCallback(
    (groups: Array<string>) => {
      setTransactionGroupings(groups);
      updateReport(groups);
      setGroupingsDialog(false);
    },
    [updateReport],
  );

  const totalTransactioned = useMemo(
    () =>
      initialTransactionsList.reduce(
        (acc, curr) =>
          acc + (curr.amount !== undefined ? curr.amount : curr.totalValue),
        0,
      ),
    [initialTransactionsList],
  );
  const totalPaid = useMemo(
    () => initialPaymentsList.reduce((acc, curr) => acc + curr.amount, 0),
    [initialPaymentsList],
  );
  const totalPending = useMemo(() => totalPaid - totalTransactioned, [
    totalPaid,
    totalTransactioned,
  ]);

  const columns = useMemo(() => {
    let columns: Array<{ key: string; name: string }> = [];
    if (transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.EMPLOYEE)) {
      columns = columns.concat([{ key: 'employeeId', name: 'Motorista' }]);
    }
    if (transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.USED_DATE)) {
      columns = columns.concat([{ key: 'usedDate', name: 'Data' }]);
    }
    if (transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.TYPE)) {
      columns = columns.concat([{ key: 'type', name: 'Tipo de Crédito' }]);
    }
    if (transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.AMOUNT)) {
      columns = columns.concat([{ key: 'amount', name: 'Valor da Transação' }]);
    }
    if (
      transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.SELLING_POINT)
    ) {
      columns = columns.concat([
        { key: 'sellingPointId', name: 'Ponto de Venda' },
      ]);
    }
    if (
      transactionGroupings.includes(
        REPORT_GROUPINGS.TRANSACTION.SELLING_TERMINAL,
      )
    ) {
      columns = columns.concat([
        { key: 'sellingTerminalId', name: 'Rede de Venda' },
      ]);
    }
    columns = columns.concat([
      { key: 'totalValue', name: 'Valor total' },
      { key: 'totalPaymentValue', name: 'Total pago' },
      { key: 'count', name: 'Quantidade' },
    ]);

    if (
      transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.QRCODE_TYPE)
    ) {
      columns = columns.concat([{ key: 'qrcodeType', name: 'Tipo de QRCode' }]);
    }

    if (
      transactionGroupings.includes(REPORT_GROUPINGS.TRANSACTION.PAYMENT_TYPE)
    ) {
      columns = columns.concat([
        { key: 'paymentType', name: 'Tipo de Pagamento' },
      ]);
    }
    return columns;
  }, [transactionGroupings]);

  useEffect(() => {
    if (currNavGuide === 0) {
      let columns: Array<{ key: string; name: string }> = [];
      if (paymentGroupings.includes(REPORT_GROUPINGS.PAYMENT.USED_DATE)) {
        columns = columns.concat([{ key: 'usedDate', name: 'Data' }]);
      }
      if (paymentGroupings.includes(REPORT_GROUPINGS.PAYMENT.AMOUNT)) {
        columns = columns.concat([{ key: 'amount', name: 'Valor' }]);
      }
      columns = columns.concat([
        { key: 'totalValue', name: 'Valor total' },
        { key: 'count', name: 'Quantidade' },
      ]);

      let headerSimplePdf =
        paymentGroupings.length === 0
          ? [
            'Data e Hora',
            'Valor',
            'Descrição',
            'Recebido Por',
            'Rede de venda',
          ]
          : columns.map((col) => col.name);
      if (transportOperatorSelected.type === 1) {
        headerSimplePdf.push('Operação');
      }

      const transformedPaymentList = initialPaymentsList.map((p: any) => {
        if (paymentGroupings.length === 0) {
          return {
            id: p.id,
            amount: p.amount,
            totalValue: p.totalValue,
            createdAt: p.createdAt,
            description: p.description,
            receivedBy: p.TransportSupervisor && p.TransportSupervisor.name,
            supervisorId: p.supervisorId,
            transportOperatorId: p.transportOperatorId,
            sellingTerminalName: p.SellingTerminal?.name,
          };
        }

        const ret: any = { ...p };
        if (p.usedDate !== undefined) {
          const [day, month, year] = p.usedDate.split('/').map(Number);
          const dateObj = new Date(year, month - 1, day);
          ret.usedDateSort = isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
        }
        return ret;
      });

      const sortedPaymentTransformed = stableSort(
        transformedPaymentList,
        getComparator(paymentOrder, paymentOrderBy),
      );

      let rowsPaymentSimplePdf =
        sortedPaymentTransformed?.length > 0
          ? sortedPaymentTransformed.map((payment: any) => [
            payment.createdAt && new Date(payment.createdAt).getTime()
              ? new Date(payment.createdAt).toLocaleString('pt-BR')
              : '-',
            {
              value: payment.amount || '0',
              stringValue:
                typeof payment.amount === 'number'
                  ? `R$ ${(payment.amount || '0').toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })}`
                  : '-',
            },
            payment?.description ? payment?.description : '-',
            payment?.receivedBy ? payment.receivedBy : '-',
            payment?.sellingTerminalName ? payment.sellingTerminalName : '-',
          ])
          : [];

      if (paymentGroupings.length > 0) {
        rowsPaymentSimplePdf =
          sortedPaymentTransformed?.length > 0
            ? sortedPaymentTransformed.map((payment: any) => {
              const row = columns.map((col) =>
                col.key === 'usedDate'
                  ? payment[col.key]
                  : {
                    value: payment[col.key],
                    stringValue: payment[col.key],
                    key: col.key,
                  },
              );

              if (transportOperatorSelected.type === 1) {
                row.push(
                  `${transportOperatorsList.find(
                    (e) => e.id === payment?.transportOperatorId,
                  )?.name
                  }` || '---',
                );
              }
              return row;
            })
            : [];
        rowsPaymentSimplePdf = rowsPaymentSimplePdf.map((e) => ['', ...e]);
        headerSimplePdf = ['', ...headerSimplePdf];
      }

      const totalRowsPaymentSimplePdf = rowsPaymentSimplePdf.reduce(
        (acc, curr, index, arr) => {
          acc[0] = 'Total';
          curr.forEach((item, i) => {
            if (item?.value !== undefined) {
              if (!acc[i]) {
                acc[i] = item.value;
              } else {
                acc[i] += item.value;
                if (index === arr.length - 1 && item?.key !== 'count') {
                  acc[i] = acc[i].toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  });
                }
              }
            } else if (!acc[i]) {
              acc[i] = '';
            }
          });
          return acc;
        },
        [],
      );

      const details = [
        {
          title: 'Transações',
          value: `${initialTransactionsList.length}`,
        },
        {
          title: 'Repasses',
          value: `${initialPaymentsList.length}`,
        },
        {
          title: 'Arrecadado',
          value: `R$ ${totalTransactioned.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
        {
          title: 'Pago',
          value: `R$ ${totalPaid.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
        {
          title: 'Pendente',
          value: `R$ ${totalPending.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
      ];

      const tempSimplePdfData = {
        title: 'ONBOARD',
        subtitle: 'Relatório de Rede de Vendas',
        filters: periodString || '',
        headers: headerSimplePdf,
        rows: [
          ...rowsPaymentSimplePdf.map((e) => e.map((e) => e?.stringValue || e)),
          totalRowsPaymentSimplePdf,
        ],
        details,
      };
      setSimplePdfData(tempSimplePdfData);
    }
  }, [
    currNavGuide,
    initialPaymentsList,
    paymentGroupings,
    periodString,
    paymentOrder,
    paymentOrderBy,
  ]);

  useEffect(() => {
    if (currNavGuide === 1) {
      const headerSimplePdf =
        transactionGroupings.length === 0
          ? [
            'Código da Transação',
            'Valor',
            'Valor Transação',
            'QRCode',
            'Dispositivo',
            'Data e Hora',
            'Tipo de Crédito',
            'Forma de Pagamento',
            'Responsável',
            'Identificador',
            'Usuário',
            'Documento',
            'Guichê',
            'Descrição',
            'Rede de venda',
          ]
          : columns.map((col) => col.name);
      if (transportOperatorSelected.type === 1) {
        headerSimplePdf.push('Operação');
      }

      const transformedList = initialTransactionsList.map((t: any) => {
        if (transactionGroupings.length === 0) {
          return {
            id: t.id && t.id.split('-')[0],
            sellingPointId: t.sellingPointId,
            sellingTerminalId: t.sellingTerminalId,
            amount: t.amount,
            paymentValue: t.paymentValue,
            paymentType: t.paymentType,
            partType: t.type,
            date: t.createdAt,
            responsible: getSellingName(t, 'responsible'),
            shortId: t.shortId || '---',
            walletChargeUserName: t.walletChargeUserName,
            walletChargeUserCPF: t?.walletChargeUserCPF || t?.tokenInfo?.document,
            description: t?.description || '',
            type: t.type || t.type === 0
              ? CONST_SELLING_POINTS.TRANSACTION_TYPES.find((e: any) => e.KEY === t.type)?.VALUE
              : '',
            qrcodeType: t.qrcodeType,
            deviceModel: t.deviceModel,
            transportOperatorId: t.transportOperatorId,
            sellingTerminalName: t.sellingTerminalName,
            sellingTerminalPlace: t.sellingTerminalPlace,
          };
        }

        const ret: any = { ...t };
        if (t.usedDate !== undefined) {
          const [day, month, year] = t.usedDate.split('/').map(Number);
          const dateObj = new Date(year, month - 1, day);
          ret.usedDateSort = isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
        }
        return ret;
      });

      const sortedTransformed = stableSort(
        transformedList,
        getComparator(transactionOrder, transactionOrderBy),
      );

      let rowsTransactionsSimplePdf =
        sortedTransformed?.length > 0
          ? sortedTransformed.map((transaction: any) => [
            transaction.id || '-',
            {
              value: transaction.amount,
              stringValue:
                typeof transaction.amount === 'number'
                  ? `R$ ${(transaction.amount || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                  : '-',
            },
            {
              value: transaction.paymentValue,
              stringValue:
                typeof transaction.paymentValue === 'number'
                  ? `R$ ${(transaction.paymentValue || '0').toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                  : '-',
            },
            transaction.qrcodeType
              ? CONST_QRCODE.ENTRY.find((e) => e.KEY === transaction.qrcodeType)?.VALUE
              : '',
            transaction?.deviceModel || '--',
            transaction.date
              ? new Date(transaction.date).toLocaleString('pt-BR')
              : '--',
            transaction.type || '--',
            transaction.paymentType === 'MULTI'
              ? CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find((e) => e.KEY === transaction.partType)?.VALUE
              : CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find((e) => e.KEY === transaction.paymentType)?.VALUE,
            transaction.responsible || '--',
            transaction?.shortId || '--',
            transaction?.walletChargeUserName || '--',
            maskCPF(transaction?.walletChargeUserCPF) || '--',
            transaction?.sellingTerminalPlace || '--',
            transaction.description || '--',
            transaction?.sellingTerminalName || '--',
          ])
          : [];

      if (transactionGroupings.length > 0) {
        rowsTransactionsSimplePdf =
          sortedTransformed?.length > 0
            ? sortedTransformed.map((transaction: any) => {
              const row = columns.map((col) => {
                const formatter = fieldFormatting.get(col.key);

                if (col.key === REPORT_GROUPINGS.TRANSACTION.SELLING_POINT) {
                  return `${sellingPointList.find(
                    (e) => e.id === transaction?.sellingPointId,
                  )?.name || '--'}`;
                }

                if (
                  col.key === REPORT_GROUPINGS.TRANSACTION.PAYMENT_TYPE &&
                  transaction.paymentType === 'MULTI'
                ) {
                  return `${CONST_TRANSPORT_VOUCHER_COLLECTION.PAYMENT_TYPE.find(
                    (item) => item.KEY === transaction.partType,
                  )?.VALUE || '--'}`;
                }

                if (col.key === REPORT_GROUPINGS.TRANSACTION.TYPE) {
                  return `${formatType(transaction[col.key])}`;
                }

                if (formatter) {
                  return col.key === 'amount' ||
                    col.key === 'totalValue' ||
                    col.key === 'totalPaymentValue'
                    ? {
                      value: transaction[col.key],
                      stringValue:
                        transaction[col.key] !== undefined
                          ? formatter(transaction[col.key])
                          : '',
                    }
                    : formatter(transaction[col.key]);
                }
                if (hasKey.includes(col.key)) {
                  return `${getSellingName(transaction, col.key)}`;
                }

                return col.key === 'count'
                  ? {
                    key: 'count',
                    value: transaction[col.key],
                    stringValue: `${transaction[col.key]}`,
                  }
                  : `${transaction[col.key]}`;
              });
              if (transportOperatorSelected.type === 1) {
                row.push(
                  `${transportOperatorsList.find(
                    (e) => e.id === transaction?.transportOperatorId,
                  )?.name
                  }` || '---',
                );
              }
              return row;
            })
            : [];
      }

      const totalRowsTransactionsSimplePdf = rowsTransactionsSimplePdf.reduce(
        (acc, curr, index, arr) => {
          acc[0] = 'Total';
          curr.forEach((item, i) => {
            if (item?.value !== undefined) {
              if (acc[i] === undefined) {
                acc[i] = item.value;
              } else {
                acc[i] += item.value;
                if (index === arr.length - 1 && item?.key !== 'count') {
                  acc[i] = `R$ ${acc[i].toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })}`;
                }
              }
            } else if (!acc[i]) {
              acc[i] = '';
            }
          });
          return acc;
        },
        [],
      );

      const details = [
        {
          title: 'Transações',
          value: `${initialTransactionsList.length}`,
        },
        {
          title: 'Repasses',
          value: `${initialPaymentsList.length}`,
        },
        {
          title: 'Arrecadado',
          value: `R$ ${totalTransactioned.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
        {
          title: 'Pago',
          value: `R$ ${totalPaid.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
        {
          title: 'Pendente',
          value: `R$ ${totalPending.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
          })}`,
        },
      ];

      const tempSimplePdfData = {
        title: 'ONBOARD',
        subtitle: 'Relatório de Rede de Vendas',
        filters: periodString || '',
        headers: headerSimplePdf,
        rows: [
          ...rowsTransactionsSimplePdf.map((e) =>
            e.map((s) => s?.stringValue ?? s),
          ),
          totalRowsTransactionsSimplePdf,
        ],
        details,
      };

      setSimplePdfData(tempSimplePdfData);
    }
  }, [
    currNavGuide,
    transactionGroupings,
    initialTransactionsList,
    periodString,
    columns,
    transactionOrder,
    transactionOrderBy,
  ]);

  useEffect(() => {
    // fill xls state
    if (setXlsHeader) {
      setXlsHeader(
        simplePdfData.headers.map((s: XlsHeaderInterface) => ({
          header: s,
          key: s,
          width: 20,
        })),
      );
    }
    if (setXlsRows) {
      setXlsRows(
        simplePdfData.rows.map((arr: string[]) =>
          arr.map((s: string, i: number) => ({
            key: simplePdfData.headers[i],
            value: s,
            image: null,
          })),
        ) as XlsRowInterface[][],
      );
    }
  }, [simplePdfData]);

  useEffect(() => {
    if (controlFilters) {
      handleApply(controlFilters);
    }
    if (
      !controlFilters &&
      currNavGuide === 1 &&
      initialTransactionsList.length === 0
    ) {
      dispatch(
        SellingTerminalsActions.listTransactionsRequest({
          startDate: new Date().setHours(0, 0, 0),
          endDate: new Date().setHours(23, 59, 59),
        }),
      );
    }
  }, [currNavGuide]);

  return (
    <div className={classes.root}>
      <Dialog open={groupingsDialog} onClose={() => setGroupingsDialog(false)}>
        <Suspense fallback={'Carregando...'}>
          <GroupingsModal
            groupingOptions={
              currNavGuide === 0 ? paymentOptions : transactionOptions
            }
            selectedOptions={
              currNavGuide === 0 ? paymentGroupings : transactionGroupings
            }
            handleGrouping={
              currNavGuide === 0
                ? handlePaymentGrouping
                : handleTransactionGrouping
            }
            setOpenModal={setGroupingsDialog}
          />
        </Suspense>
      </Dialog>
      <MainContent className={classes.paperRoot}>
        <Grid className={classes.gridContent} container spacing={2}>
          <Grid item xs={12} ref={(ref: any) => setTableReference(ref)}>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <ReportTitle
                  titleVariant='h4'
                  title='Relatórios de Arrecadação • Rede de Vendas'
                  tooltipTitle={
                    TITLES_OPTIONS.REPORTS.COLLECTION.SALES_NETWORK.VALUE
                  }
                />
              </Grid>
              {reportIsVisible && (
                <Grid item>
                  <CSVLink
                    filename={`Relatório-Arrecadação-${getCurrentNavigation()}.csv`}
                    data={tableCSV}
                    ref={csvRef}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <Grid container spacing={1} justifyContent='flex-start'>
                  {/* <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Box
                        display='flex'
                        alignItems='center'
                        width='100%'
                        marginTop='8px'>

                        <Grid container justifyContent='flex-end'>
                          <Button
                            className={classes.addButton}
                            onClick={() => setIsAddSubmissiion(true)}>
                            <Typography
                              style={{ marginRight: 8, fontSize: 20 }}>
                              +
                            </Typography>{' '}
                            <Typography style={{ fontSize: 12 }}>
                              Programar Novo
                            </Typography>
                          </Button>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid> */}
                  <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Box
                        display='flex'
                        width='100%'
                        mt={2}
                        ml={2}
                        maxWidth='580px'>
                        <SearchComponent
                          currNavGuide={currNavGuide}
                          disabledInput={!reportIsVisible}
                          data={
                            currNavGuide === 0
                              ? initialPaymentsList
                              : initialTransactionsList
                          }
                          itemsByFilter={ITEMS.ReportOnboardSales}
                          results={(data: any[]) => {
                            if (currNavGuide === 0) {
                              setPaymentsList(data);
                            } else {
                              setTransactionsList(data);
                            }
                          }}
                          modalFilter={() => {
                            dispatch(
                              setOpen({
                                open: true,
                                content: (
                                  <Suspense fallback={<p>Carregando...</p>}>
                                    <Filter
                                      fields={
                                        currNavGuide === 0
                                          ? ITEMS.collection.paymentTabRemote
                                          : ITEMS.collection
                                            .transactionsTabRemote
                                      }
                                      handleApply={handleApply}
                                    />
                                  </Suspense>
                                ),
                                maxWidth: 'xl',
                              }),
                            );
                          }}
                        />
                        {reportIsVisible && generalNavGuide === 0 && (
                          <Typography
                            component='span'
                            onClick={() => {
                              if (!reportIsVisible) return;
                              setGroupingsDialog(true);
                            }}>
                            <Box className={classes.contentGroupings}>
                              <Box className={classes.groupings}>
                                <img
                                  src={IMAGES.ICONS.GROUPINGS}
                                  alt='groupings'
                                />
                              </Box>
                              {currNavGuide === 0 &&
                                !!paymentGroupings.length && (
                                  <Box className={classes.badge}>
                                    {paymentGroupings.length}
                                  </Box>
                                )}
                              {currNavGuide === 1 &&
                                !!transactionGroupings.length && (
                                  <Box className={classes.badge}>
                                    {transactionGroupings.length}
                                  </Box>
                                )}
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Tabs
              indicatorColor='primary'
              onChange={handleChange}
              style={{
                background: '#ffffff',
                margin: '10px 0px 32px 0px',
              }}
              textColor='primary'
              value={generalNavGuide}>
              <Tab label='Relatório' />
              <Tab label='Envios' />
              <Tab label='Histórico' />
            </Tabs>
            <TabPanel index={0} value={generalNavGuide}>
              <Grid item xs={12}>
                {!reportIsVisible && (
                  <TablePlaceholder onClick={handleLoadData} />
                )}
                {/* Navigation Guide */}
                {reportIsVisible && (
                  <>
                    <Grid
                      container
                      justifyContent='flex-end'
                      alignItems='center'>
                      <Box display='flex' alignItems='center'>
                        <Box className={classes.resume}>
                          <Box className={classes.contentImage}>
                            <img
                              src={IMAGES.REPORTS_ICONS.TICKET_SOLD}
                              alt='ticket sold'
                            />
                          </Box>
                          <Divider
                            className={classes.dividerVertical}
                            orientation='vertical'
                            flexItem
                          />{' '}
                          <Grid className={classes.contentTotalEntry}>
                            <Typography className={classes.subTiltle}>
                              Transações
                            </Typography>
                            <Typography className={classes.value}>
                              {initialTransactionsList.length}
                            </Typography>
                          </Grid>
                        </Box>
                        <Box className={classes.resume}>
                          <Box className={classes.contentImage}>
                            <img
                              src={IMAGES.REPORTS_ICONS.CREDIT_PORTFOLIO}
                              alt='credit portfolio'
                            />
                          </Box>
                          <Divider
                            className={classes.dividerVertical}
                            orientation='vertical'
                            flexItem
                          />{' '}
                          <Grid className={classes.contentTotalEntry}>
                            <Typography className={classes.subTiltle}>
                              Repasses
                            </Typography>
                            <Typography className={classes.value}>
                              {initialPaymentsList.length}
                            </Typography>
                          </Grid>
                        </Box>
                        <Box className={classes.resume}>
                          <Box className={classes.contentImage}>
                            <img
                              src={IMAGES.REPORTS_ICONS.TOTAL_ENTRY}
                              alt='total entry'
                            />
                          </Box>
                          <Divider
                            className={classes.dividerVertical}
                            orientation='vertical'
                            flexItem
                          />{' '}
                          <Grid className={classes.contentTotalEntry}>
                            <Typography className={classes.subTiltle}>
                              Arrecadado
                            </Typography>
                            <Typography className={classes.value}>
                              {`R$ ${totalTransactioned.toLocaleString(
                                'pt-BR',
                                {
                                  minimumFractionDigits: 2,
                                },
                              )}`}
                            </Typography>
                          </Grid>
                          <Divider
                            className={classes.dividerVertical}
                            orientation='vertical'
                            flexItem
                          />{' '}
                          <Grid className={classes.contentTotalEntry}>
                            <Typography className={classes.subTiltle}>
                              Pago
                            </Typography>
                            <Typography className={classes.value}>
                              {`R$ ${totalPaid.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}`}
                            </Typography>
                          </Grid>
                          <Divider
                            className={classes.dividerVertical}
                            orientation='vertical'
                            flexItem
                          />{' '}
                          <Grid className={classes.contentTotalEntry}>
                            <Typography className={classes.subTiltle}>
                              Pendente
                            </Typography>
                            <Typography className={classes.value}>
                              {`R$ ${totalPending.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}`}
                            </Typography>
                          </Grid>
                        </Box>
                        <DialogueExport
                          tableInstance={tableReference}
                          csvRef={csvRef}
                          simplePdfData={simplePdfData}
                          columnsPerPage={5}
                          totalRows
                          pdfName={
                            currNavGuide === 0
                              ? 'relatorio_arrecadacao_rede_de_vendas_repasses'
                              : 'relatorio_arrecadacao_rede_de_vendas_transações'
                          }
                          xlsTitle={
                            currNavGuide === 0
                              ? 'relatorio_arrecadacao_rede_de_vendas_repasses'
                              : 'relatorio_arrecadacao_rede_de_vendas_transações'
                          }
                          xlsHeader={xlsHeader}
                          xlsRows={xlsRows}
                          simplePdf
                          xls
                          csv
                        />
                      </Box>
                    </Grid>
                    {currNavGuide === 0 && initialPaymentsList?.length! > 0 && (
                      <Box
                        width='100%'
                        display='flex'
                        justifyContent='flex-end'>
                        <Suspense fallback='Carregando...'>
                          <TotalCountComponent
                            count={initialPaymentsList?.length! || 0}
                            title='repasses'
                            position='right'
                          />
                        </Suspense>
                      </Box>
                    )}

                    {currNavGuide === 1 &&
                      initialTransactionsList?.length! > 0 && (
                        <Box
                          width='100%'
                          display='flex'
                          justifyContent='flex-end'>
                          <Suspense fallback='Carregando...'>
                            <TotalCountComponent
                              count={initialTransactionsList?.length! || 0}
                              title='transações'
                              position='right'
                            />
                          </Suspense>
                        </Box>
                      )}

                    <CustomTabs
                      setCurrNavGuide={setCurrNavGuide}
                      currNavGuide={currNavGuide}
                      labels={['Repasses', 'Transações']}
                    />
                    <SwipeableViews
                      axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                      index={currNavGuide}
                      onChangeIndex={setCurrNavGuide}>
                      {/* Repasses */}
                      <TabPanel index={0} value={currNavGuide}>
                        <Suspense fallback={'Carregando...'}>
                          <SellingPaymentNavigation
                            groupings={paymentGroupings}
                            setTableCSV={setTableCSV}
                            setTableReference={setTableReference}
                            paymentsList={paymentsList}
                            setPaymentOrder={setPaymentOrder}
                            setPaymentOrderBy={setPaymentOrderBy}
                          />
                        </Suspense>
                      </TabPanel>
                      {/* Transações */}
                      <TabPanel index={1} value={currNavGuide}>
                        <Suspense fallback={'Carregando...'}>
                          <SellingTransactionNavigation
                            groupings={transactionGroupings}
                            setTableCSV={setTableCSV}
                            setTableReference={setTableReference}
                            transactionsList={transactionsList}
                            fieldFormatting={fieldFormatting}
                            groupColumns={columns}
                            setTransactionOrder={setTransactionOrder}
                            setTransactionOrderBy={setTransactionOrderBy}
                          />
                        </Suspense>
                      </TabPanel>
                    </SwipeableViews>
                  </>
                )}
              </Grid>
            </TabPanel>
            <TabPanel index={1} value={generalNavGuide}>
              <Suspense fallback={'Carregando...'}>
                <Submissions
                  setSubmissionData={setSubmissionData}
                  setIsEditSubmissiion={setIsEditSubmissiion}
                  setIsAddSubmissiion={setIsAddSubmissiion}
                  reportType={REPORTS_TYPES.SELLING_TERMINAL_PAYMENT}
                  secondReportType={REPORTS_TYPES.SELLING_TRANSACTION}
                />
              </Suspense>
            </TabPanel>{' '}
            <TabPanel index={2} value={generalNavGuide}>
              <Suspense fallback={'Carregando...'}>
                <History
                  reportType={REPORTS_TYPES.SELLING_TERMINAL_PAYMENT}
                  secondReportType={REPORTS_TYPES.SELLING_TRANSACTION}
                />
              </Suspense>
            </TabPanel>
          </Grid>
        </Grid>
      </MainContent>
    </div>
  );
};

export { CollectionReportDatails };
export default CollectionReportDatails;
